import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { flattenObject } from '../utils/utils';
import { ApiGetSubaccounts, PasswordType, Subaccount, Subaccounts } from '@models/subaccount/subaccount';
import { CreditMovementsList } from '@models/subaccount/credit-movements';

@Injectable({
  providedIn: 'root'
})
export class SubaccountService {
  private readonly baseUrl;

  // private readonly prefixLabelCreditType: string = 'credit_type_';

  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  addSubaccount(subaccount: Subaccount): Observable<Subaccount> {
    return this.http.post(this.baseUrl + 'subaccount', flattenObject(subaccount)).pipe(map(data => Subaccount.deserialize(data)));
  }

  editSubaccount(idSubaccount, patch): Observable<any> {
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http.patch<Response>(this.baseUrl + 'subaccount/' + idSubaccount, patch, { headers: patchHeaders });
  }

  editSubaccountPassword(idSubaccount: number, newPassword: string, passwordType: PasswordType) {
    const body = {
      idSubaccount,
      newPassword,
      passwordType
    };
    return this.http.post<Response>(this.baseUrl + 'subaccount/change-password', body);
  }

  getSubaccount(idSubaccount): Observable<Subaccount> {
    return this.http.get<Response>(this.baseUrl + 'subaccount/' + idSubaccount).pipe(map(data => Subaccount.deserialize(data)));
  }

  getSubaccounts(pageNum?, pageSize?, sortColumn?, sortDirection?, body?: ApiGetSubaccounts): Observable<Subaccounts> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    } else {
      params = params.append('page', '1');
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    } else {
      params = params.append('size', '10');
    }
    if (sortColumn) {
      params = params.append('sortColumn', sortColumn);
    } else {
      params = params.append('sortColumn', 'ID_USER');
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    } else {
      params = params.append('sortDirection', 'a');
    }

    return this.http
      .post(this.baseUrl + 'subaccount/search', body, {
        params
      })
      .pipe(
        map(data => {
          return Subaccounts.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 => Subaccount.deserialize(data2)),
            total: (data as any).total
          });
        })
      );
  }

  lockUnlockSubaccount(idSubaccountUser: number, isLock: boolean) {
    const body = {
      idSubaccountUser,
      isLock
    };
    return this.http.post<Response>(this.baseUrl + '/subaccount/lock-unlock-subaccount', body);
  }

  transferCredits(body) {
    return this.http.post(this.baseUrl + '/subaccount/move-credits', body);
  }

  createPurchase(body) {
    return this.http.post(this.baseUrl + '/subaccount/create-purchase', body);
  }

  // return the history of the subaccount credit movement
  listCreditMovements(
    idSubaccount: string,
    page?: number,
    size?: number,
    sortColumn?: string,
    sortDirection?: string
  ): Observable<CreditMovementsList> {
    let queryParams = new HttpParams();
    if (page) {
      queryParams = queryParams.set('page', String(page));
    }
    if (size) {
      queryParams = queryParams.set('size', String(size));
    }
    if (sortColumn) {
      queryParams = queryParams.set('sortColumn', String(sortColumn));
    }
    if (sortDirection) {
      queryParams = queryParams.set('sortDirection', String(sortDirection));
    }
    const url = 'subaccount/' + idSubaccount + '/listCreditMovements';

    return this.http
      .get<Response>(this.baseUrl + url, { params: queryParams })
      .pipe(map(data => CreditMovementsList.deserialize(data)));
  }

  listMyCreditMovements(page?: number, size?: number, sortColumn?: string, sortDirection?: string): Observable<CreditMovementsList> {
    let queryParams = new HttpParams();
    if (page) {
      queryParams = queryParams.set('page', String(page));
    }
    if (size) {
      queryParams = queryParams.set('size', String(size));
    }
    if (sortColumn) {
      queryParams = queryParams.set('sortColumn', String(sortColumn));
    }
    if (sortDirection) {
      queryParams = queryParams.set('sortDirection', String(sortDirection));
    }

    return this.http
      .get<Response>(this.baseUrl + 'subaccount/listCreditMovements', { params: queryParams })
      .pipe(map(data => CreditMovementsList.deserialize(data)));
  }

  deleteCreditMovement(idSubaccountUser: number, idPurchase: number): Observable<any> {
    const body = { idSubaccountUser, idPurchase };
    const options = { responseType: 'text' as 'json', body };
    const url = '/subaccount/listCreditMovements/deleteVirtualCredits';
    return this.http.delete<Response>(this.baseUrl + url, options);
  }
}

export const PrefixLabels = {
  CREDIT_TYPE: 'credit_type_'
};
